import type { ImportConflict, ImportReport, SchemaJson } from '@/api/types/core/schemaJson';
import type { NewSectionTranslation, NewSectionTranslationConflict, SchemaTranslationResponse } from '@/api/types/core/schemaTranslations';
import type { TranslationCreate } from '@/api/types/core/translations';
import { reactive, ref } from 'vue';
import { schemaTranslations, schemas, sections, translations } from '../api';
import { fetchConfigsAdmin } from '../api/field_config';
import {
  detectSimilarSchemas,
  detectSimilarSectionNames,
  type SimilarSchema,
  type SimilarSection
} from '../utils/sectionSimilarity';
import { SectionUtils } from '../utils/sectionUtils';

export interface SchemaConflict {
  type: 'uuid_match' | 'name_match' | 'similar_schema' | 'none';
  existing_schema?: {
    uuid: string;
    appellation: string;
    type: string;
    created_at?: string;
  };
  similar_schemas?: SimilarSchema[];
  action?: 'update' | 'create_new' | 'align_existing';
  similarity_percentage?: number;
}

export interface SchemaChanges {
  sectionsToAdd: unknown[];
  sectionsToRemove: unknown[];
  sectionsUnchanged: unknown[];
  translationsToAdd: unknown[];
  translationsToUpdate: unknown[];
  translationsToRemove: unknown[];
}

export interface ParsedSchemaWithAnalysis extends SchemaJson {
  _analysisReport?: {
    schema: SchemaJson;
    sections: {
      new_sections: unknown[];
      existing_sections: unknown[];
      similar_sections: unknown[];
      hierarchy_conflicts: unknown[];
    };
    translation_conflicts: unknown[];
    new_section_translation_conflicts?: NewSectionTranslationConflict[];
    removed_sections?: Array<{
      id: number;
      name: string;
      type?: string;
      has_translations: boolean;
      usage_count?: number;
    }>;
    similar_sections_detected?: Array<{
      imported: { id: string };
      similar_candidates: Array<{
        section: { id: number; name: string; type?: string };
        similarity: number;
        reason: string;
      }>;
    }>;
    summary: {
      new_sections: number;
      existing_sections: number;
      similar_sections: number;
      hierarchy_conflicts: number;
      translation_conflicts: number;
      new_section_translation_conflicts?: number;
      removed_sections?: number;
      similar_sections_detected?: number;
    };
  };
  _detailedConflicts?: ImportConflict[];
}

export function useSchemaImport(): {
  step: import('vue').Ref<number>;
  selectedFile: import('vue').Ref<File | null>;
  parsedSchema: import('vue').Ref<ParsedSchemaWithAnalysis | null>;
  importReport: import('vue').Ref<ImportReport | null>;
  errorMessage: import('vue').Ref<string>;
  parsing: import('vue').Ref<boolean>;
  importing: import('vue').Ref<boolean>;
  schemaConflict: import('vue').Ref<SchemaConflict | null>;
  schemaChanges: import('vue').Ref<SchemaChanges | null>;
  editableSchema: {
    appellation: string;
    type: string;
    description: string;
  };
  resolutions: Record<string, string>;
  translationResolutions: Record<string, string>;
  newSectionTranslationResolutions: Record<string, string>;
  similarSectionChoices: Record<string, { 
    action: 'create_new' | 'align_with_existing'; 
    target_section?: string;
  }>;
  newSectionActions: Record<string, 'create' | 'skip'>;
  excludedSections: Set<string>;
  keepRemovedSections: Set<number>;
  keepRemovedTranslations: Set<string>;
  updateTranslationResolutions: Record<string, string>;
  parseFile: () => Promise<void>;
  executeImport: () => Promise<void>;
  reset: () => void;
  updateResolution: (conflictId: string, resolution: string) => void;
  updateTranslationResolution: (sectionName: string, languageCode: string, fieldType: string, resolution: string) => void;
  updateNewSectionTranslationResolution: (sectionName: string, languageCode: string, resolution: string) => void;
  getNewSectionTranslationResolution: (conflict: { section_name: string; language_code: string }) => string;
  updateSimilarSectionChoice: (sectionId: string, action: 'create_new' | 'align_with_existing', targetSection?: string) => void;
  updateNewSectionAction: (sectionId: string, action: 'create' | 'skip') => void;
  updateRemovedSectionAction: (sectionId: number, action: 'keep' | 'remove') => void;
} {
  // State principal
  const step = ref(1);
  const selectedFile = ref<File | null>(null);
  const parsedSchema = ref<ParsedSchemaWithAnalysis | null>(null);
  const importReport = ref<ImportReport | null>(null);
  const errorMessage = ref('');
  const parsing = ref(false);
  const importing = ref(false);
  
  // State pour la gestion des conflits de schémas
  const schemaConflict = ref<SchemaConflict | null>(null);
  const schemaChanges = ref<SchemaChanges | null>(null);
  
  // State pour les métadonnées éditables du schéma
  const editableSchema = reactive({
    appellation: '',
    type: '',
    description: ''
  });
  
  // State pour les résolutions de conflits
  const resolutions = reactive<Record<string, string>>({});
  const translationResolutions = reactive<Record<string, string>>({});
  const newSectionTranslationResolutions = reactive<Record<string, string>>({});
  const similarSectionChoices = reactive<Record<string, { 
    action: 'create_new' | 'align_with_existing'; 
    target_section?: string;
  }>>({});
  const newSectionActions = reactive<Record<string, 'create' | 'skip'>>({});
  const excludedSections = reactive<Set<string>>(new Set());
  const keepRemovedSections = reactive<Set<number>>(new Set());
  const keepRemovedTranslations = reactive<Set<string>>(new Set());
  const updateTranslationResolutions = reactive<Record<string, string>>({});

  // Analyse du fichier
  const parseFile = async (): Promise<void> => {
    if (!selectedFile.value) return;
    
    parsing.value = true;
    errorMessage.value = '';
    
    try {
      const content = await selectedFile.value.text();
      const parsed = JSON.parse(content) as SchemaJson;
      
      // Validation basique du schéma
      if (!parsed.appellation || !parsed.type || !Array.isArray(parsed.section_list)) {
        throw new Error('Format de schéma invalide. Propriétés requises: appellation, type, section_list');
      }
      
      console.log('📥 Schéma analysé:', parsed.appellation);
      
      // Récupérer les données existantes pour l'analyse
      const [existingSections, existingTranslations, existingSchemas] = await Promise.all([
        sections.fetchAllSections(),
        translations.fetchTranslations(),
        schemas.fetchAllSchemas()
      ]);
      
      // Analyse avancée des sections
      const sectionAnalysis = SectionUtils.analyzeImportSections(
        parsed.section_list,
        existingSections
      );
      
      // Détection de sections similaires avec notre nouvelle logique
      const similarSectionsDetected: Array<{
        imported: { id: string };
        similar_candidates: SimilarSection[];
      }> = [];
      
      for (const newSection of sectionAnalysis.new_sections) {
        const importedSection = newSection as { id: string };
        const similarSections = detectSimilarSectionNames(
          importedSection.id,
          existingSections,
          0.7 // Seuil de 70%
        );
        
        if (similarSections.length > 0) {
          similarSectionsDetected.push({
            imported: importedSection,
            similar_candidates: similarSections
          });
          
          // Initialiser le choix par défaut (créer nouvelle section)
          similarSectionChoices[importedSection.id] = {
            action: 'create_new'
          };
        }
      }
      
      // Convertir nos détections au format attendu pour l'analyse
      const convertedSimilarSections = similarSectionsDetected.map(detection => ({
        imported: detection.imported,
        similar_candidates: detection.similar_candidates.map(candidate => ({
          section: {
            id: candidate.section.id,
            name: candidate.section.name,
            type: candidate.section.type
          },
          similarity: candidate.similarity,
          reason: candidate.reason
        }))
      }));
      
      // Extraire les traductions du schéma importé
      const importedTranslations: TranslationCreate[] = [];
      const newSectionTranslations: NewSectionTranslation[] = [];
      
      // Créer une map des sections existantes pour obtenir les IDs
      const sectionNameToId = new Map(existingSections.map(s => [s.name, s.id]));
      
      for (const section of parsed.section_list) {
        if (section.translations) {
          const sectionId = sectionNameToId.get(section.id);
          if (sectionId) {
            // Section existante - logique normale
            for (const [langCode, translationData] of Object.entries(section.translations)) {
              importedTranslations.push({
                reference_id: sectionId,
                language_code: langCode,
                titre: translationData.titre || '',
                description: translationData.description || ''
              });
            }
          } else {
            // Nouvelle section avec traductions
            newSectionTranslations.push({
              section_name: section.id,
              translations: section.translations
            });
          }
        }
      }
      
      // Récupérer les traductions schéma existantes si c'est une mise à jour
      let existingSchemaTranslations: SchemaTranslationResponse[] = [];
      const potentialSchemaConflict = detectExistingSchema(parsed, existingSchemas);
      
      if (potentialSchemaConflict.type !== 'none' && potentialSchemaConflict.existing_schema) {
        try {
          existingSchemaTranslations = await schemaTranslations.fetchSchemaTranslations(potentialSchemaConflict.existing_schema.uuid);
          console.log(`📚 ${existingSchemaTranslations.length} traductions schéma récupérées pour l'analyse`);
        } catch (error) {
          console.warn('⚠️ Erreur lors de la récupération des traductions schéma:', error);
          existingSchemaTranslations = [];
        }
      }

      // Détection des conflits de traductions pour sections existantes
      const translationConflicts = SectionUtils.detectTranslationConflicts(
        importedTranslations,
        existingTranslations,
        existingSchemaTranslations,
        existingSections
      );

      // Détection des conflits de traductions pour nouvelles sections
      const newSectionTranslationConflicts: NewSectionTranslationConflict[] = [];
      for (const newSectionTranslation of newSectionTranslations) {
        for (const [langCode, translationData] of Object.entries(newSectionTranslation.translations)) {
          const typedTranslationData = translationData as { titre?: string; description?: string };
          if (typedTranslationData.titre || typedTranslationData.description) {
            newSectionTranslationConflicts.push({
              section_name: newSectionTranslation.section_name,
              language_code: langCode,
              imported_translation: {
                titre: typedTranslationData.titre || '',
                description: typedTranslationData.description || ''
              },
              resolution: 'create_schema_specific' // Résolution par défaut
            });
          }
        }
      }
      
      // Créer le rapport d'analyse
      const analysisReport = {
        schema: parsed,
        sections: sectionAnalysis,
        translation_conflicts: translationConflicts,
        new_section_translation_conflicts: newSectionTranslationConflicts,
        removed_sections: [] as Array<{
          id: number;
          name: string;
          type?: string;
          has_translations: boolean;
          usage_count?: number;
        }>,
        similar_sections_detected: convertedSimilarSections,
        summary: {
          new_sections: sectionAnalysis.new_sections.length - similarSectionsDetected.length,
          existing_sections: sectionAnalysis.existing_sections.length,
          similar_sections: sectionAnalysis.similar_sections?.length || 0,
          hierarchy_conflicts: sectionAnalysis.hierarchy_conflicts.length,
          translation_conflicts: translationConflicts.length,
          new_section_translation_conflicts: newSectionTranslationConflicts.length,
          removed_sections: 0,
          similar_sections_detected: similarSectionsDetected.length
        }
      };
      
      // Stocker l'analyse dans le schéma
      const parsedWithAnalysis = parsed as ParsedSchemaWithAnalysis;
      parsedWithAnalysis._analysisReport = analysisReport;
      
      // Détecter les conflits de propriétés pour les sections existantes
      const detailedConflicts: ImportConflict[] = [];
      
      for (const { imported, existing } of sectionAnalysis.existing_sections) {
        const conflictsForSection = await detectDetailedConflicts(imported, existing);
        detailedConflicts.push(...conflictsForSection);
      }
      
      parsedWithAnalysis._detailedConflicts = detailedConflicts;
      
      // Initialiser les résolutions par défaut
      initializeResolutions(detailedConflicts, translationConflicts, newSectionTranslationConflicts, sectionAnalysis);
      
      parsedSchema.value = parsedWithAnalysis;
      
      // Initialiser les métadonnées éditables
      editableSchema.appellation = parsed.appellation || '';
      editableSchema.type = validateSchemaType(parsed.type) || '';
      editableSchema.description = parsed.description || '';
      
      // Analyser si le schéma existe déjà
      const schemaConflictResult = detectExistingSchema(parsed, existingSchemas);
      schemaConflict.value = schemaConflictResult.type !== 'none' ? schemaConflictResult : null;
      
      // Si c'est une mise à jour, détecter les sections supprimées
      if (schemaConflict.value && schemaConflict.value.existing_schema) {
        try {
          const removedSections = await detectRemovedSections(
            schemaConflict.value.existing_schema.uuid,
            parsed.section_list,
            existingTranslations
          );
          
          analysisReport.removed_sections = removedSections;
          analysisReport.summary.removed_sections = removedSections.length;
          
          // Initialiser les sections supprimées comme non conservées par défaut
          removedSections.forEach((section: { id: number }) => {
            keepRemovedSections.delete(section.id); // S'assurer qu'elles ne sont pas conservées par défaut
          });
          
        } catch (error) {
          console.warn('⚠️ Erreur lors de la détection des sections supprimées:', error);
          analysisReport.removed_sections = [];
          analysisReport.summary.removed_sections = 0;
        }
      }
      
      step.value = 2;
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'analyse du fichier:', error);
      errorMessage.value = error instanceof Error ? error.message : 'Erreur inconnue lors de l\'analyse';
    } finally {
      parsing.value = false;
    }
  };

  // Import réel du schéma
  const executeImport = async (): Promise<void> => {
    if (!parsedSchema.value) return;
    
    importing.value = true;
    errorMessage.value = '';
    
    try {
      console.log('🚀 Début de l\'import du schéma:', parsedSchema.value.appellation);
      
      const report: ImportReport = {
        success: true,
        sections_created: 0,
        sections_updated: 0,
        translations_created: 0,
        translations_updated: 0,
        conditions_created: 0,
        conditions_updated: 0,
        conflicts: []
      };
      
      // 1. Récupérer les sections existantes
      const existingSections = await sections.fetchAllSections();
      const existingSectionsMap = new Map(existingSections.map(s => [s.name, s]));
      
      // 2. Traiter les sections (triées par profondeur)
      const sortedSections = [...parsedSchema.value.section_list].sort((a, b) => {
        const depthA = SectionUtils.getDepthLevel(a.id);
        const depthB = SectionUtils.getDepthLevel(b.id);
        return depthA - depthB;
      });
      
      for (const importedSection of sortedSections) {
        const sectionName = importedSection.id;
        const existingSection = existingSectionsMap.get(sectionName);
        
        // Vérifier si cette nouvelle section doit être ignorée selon les choix de l'utilisateur
        if (!existingSection) {
          const action = newSectionActions[sectionName] || 'create';
          if (action === 'skip') {
            console.log(`⏭️ Nouvelle section ignorée par choix utilisateur: ${sectionName}`);
            continue;
          }
        }
        
        // Gérer l'alignement avec des sections existantes
        const similarChoice = similarSectionChoices[sectionName];
        if (similarChoice && similarChoice.action === 'align_with_existing' && similarChoice.target_section) {
          const targetSection = existingSectionsMap.get(similarChoice.target_section);
          if (targetSection) {
            existingSectionsMap.set(sectionName, targetSection);
            continue;
          }
        }
        
        try {
          if (existingSection) {
            // Mettre à jour la section existante si nécessaire
            const hasChanges = (
              existingSection.type !== (importedSection.type || '') ||
              existingSection.mandatory_entry !== (importedSection.mandatory_entry || false) ||
              existingSection.display !== (importedSection.display ?? true)
            );
            
            if (hasChanges) {
              const updateData = {
                ...existingSection,
                type: importedSection.type || '',
                mandatory_entry: importedSection.mandatory_entry || false,
                display: importedSection.display ?? true
              };
              
              await sections.updateSection(existingSection.id, updateData);
              report.sections_updated++;
            }
            
          } else {
            // Créer nouvelle section
            await ensureHierarchyExists(sectionName, existingSectionsMap);
            
            const newSectionData = {
              name: sectionName,
              type: importedSection.type || '',
              display: importedSection.display ?? true,
              ordre: 1,
              importance: 0,
              mandatory_entry: importedSection.mandatory_entry || false
            };
            
            const createdSections = await sections.createSections([newSectionData]);
            const createdSection = createdSections[0];
            
            existingSectionsMap.set(sectionName, createdSection);
            report.sections_created++;
          }
          
          // Traiter les traductions de la section
          await processTranslations(importedSection, existingSectionsMap, report);
          
          // Traiter les conditions
          await processConditions(importedSection, existingSectionsMap, report);
          
        } catch (sectionError) {
          console.error(`❌ Erreur traitement section ${sectionName}:`, sectionError);
        }
      }
      
      // Récupérer les conflits détaillés avec cast correct du type
      const detailedConflicts = parsedSchema.value._detailedConflicts || [];
      report.conflicts = detailedConflicts.map((conflict: ImportConflict) => ({
        ...conflict,
        resolution: (resolutions[conflict.id] || conflict.resolution) as ImportConflict['resolution']
      }));
      
      // Créer le schéma dans la base de données
      await createSchemaInDatabase(parsedSchema.value, existingSectionsMap, report);
      
      importReport.value = report;
      step.value = 4;
      
      console.log('✅ Import terminé avec succès');
      
    } catch (error: unknown) {
      console.error('❌ Erreur import:', error);
      const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
      errorMessage.value = `Erreur lors de l'import: ${errorMsg}`;
    } finally {
      importing.value = false;
    }
  };

  // Utilitaires privées
  const detectDetailedConflicts = async (
    imported: { type?: string; mandatory_entry?: boolean }, 
    existing: { id: number; name: string; type?: string; mandatory_entry?: boolean }
  ): Promise<ImportConflict[]> => {
    const conflicts: ImportConflict[] = [];
    
    if (imported.type !== undefined && imported.type !== existing.type) {
      conflicts.push({
        id: `${existing.id}_type`,
        type: 'section_property',
        section_id: existing.name,
        field_name: 'type',
        existing_value: existing.type || '',
        imported_value: imported.type || '',
        merged_value: imported.type || existing.type,
        resolution: 'use_imported' as const,
        description: `Type différent: "${existing.type || '(vide)'}" vs "${imported.type || '(vide)'}"`,
        can_edit_merge: true,
        merge_type: 'text' as const
      });
    }
    
    return conflicts;
  };

  const initializeResolutions = (
    detailedConflicts: ImportConflict[], 
    translationConflicts: unknown[], 
    newSectionTranslationConflicts: NewSectionTranslationConflict[],
    sectionAnalysis: { 
      similar_sections: Array<{ imported: { id: string } }>; 
      new_sections: Array<{ id: string }> 
    }
  ): void => {
    // Réinitialiser les résolutions
    Object.keys(resolutions).forEach(key => delete resolutions[key]);
    Object.keys(translationResolutions).forEach(key => delete translationResolutions[key]);
    Object.keys(newSectionTranslationResolutions).forEach(key => delete newSectionTranslationResolutions[key]);
    Object.keys(similarSectionChoices).forEach(key => delete similarSectionChoices[key]);
    Object.keys(newSectionActions).forEach(key => delete newSectionActions[key]);
    
    // Initialiser les résolutions par défaut
    detailedConflicts.forEach(conflict => {
      resolutions[conflict.id] = conflict.resolution;
    });
    
    // Pour les conflits de traduction, analyser si c'est vraiment un conflit
    (translationConflicts as Array<{ 
      section_name: string; 
      language_code: string; 
      field_type?: string;
      existing_translation?: { titre: string; description: string };
      imported_translation: { titre: string; description: string };
    }>).forEach(conflict => {
      // Utiliser les mêmes clés que dans processTranslations
      const keyAll = `${conflict.section_name}_${conflict.language_code}_all`;
      
      // Vérifier si les traductions sont identiques
      const existingTitle = conflict.existing_translation?.titre?.trim() || '';
      const importedTitle = conflict.imported_translation?.titre?.trim() || '';
      const existingDesc = conflict.existing_translation?.description?.trim() || '';
      const importedDesc = conflict.imported_translation?.description?.trim() || '';
      
      if (existingTitle === importedTitle && existingDesc === importedDesc) {
        // Traductions identiques, utiliser la globale existante
        translationResolutions[keyAll] = 'use_global';
        console.log(`🔄 Traductions identiques détectées pour ${conflict.section_name} (${conflict.language_code}), utilisation de la traduction globale`);
      } else {
        // Vraie différence, créer une traduction schéma-spécifique
        translationResolutions[keyAll] = 'create_schema_specific';
        console.log(`⚠️ Conflit de traduction détecté pour ${conflict.section_name} (${conflict.language_code}), création d'une traduction schéma-spécifique`);
      }
    });
    
    // Initialiser les résolutions pour les nouvelles traductions
    newSectionTranslationConflicts.forEach(conflict => {
      const key = `${conflict.section_name}_${conflict.language_code}_all`;
      newSectionTranslationResolutions[key] = conflict.resolution;
      console.log(`🆕 Nouvelle traduction détectée pour ${conflict.section_name} (${conflict.language_code}), résolution par défaut: ${conflict.resolution}`);
    });
    
    sectionAnalysis.similar_sections.forEach((item) => {
      similarSectionChoices[item.imported.id] = { action: 'create_new' };
    });
    
    sectionAnalysis.new_sections.forEach((section) => {
      newSectionActions[section.id] = 'create';
    });
    
    excludedSections.clear();
  };

  const processTranslations = async (
    importedSection: { 
      id: string; 
      translations?: Record<string, { titre?: string; description?: string }> 
    }, 
    existingSectionsMap: Map<string, { id: number }>, 
    report: ImportReport
  ): Promise<void> => {
    if (!importedSection.translations) return;
    
    const currentSection = existingSectionsMap.get(importedSection.id);
    if (!currentSection) return;
    
    const allTranslations = await translations.fetchTranslations() as unknown[];
    const existingTranslations = allTranslations.filter((t: unknown) => {
      const translation = t as { reference_id: number; language_code: string; titre: string; description: string; id: number };
      return translation.reference_id === currentSection.id;
    });
    
    const existingTranslationsMap = new Map(
      existingTranslations.map((t: unknown) => {
        const translation = t as { language_code: string; titre: string; description: string; id: number };
        return [translation.language_code, translation];
      })
    );
    
    for (const [langCode, translationData] of Object.entries(importedSection.translations)) {
      const existingTranslation = existingTranslationsMap.get(langCode) as { 
        titre: string; 
        description: string; 
        id: number; 
      } | undefined;
      
      const conflictKeyTitle = `${importedSection.id}_${langCode}_titre`;
      const conflictKeyDescription = `${importedSection.id}_${langCode}_description`;
      const conflictKeyAll = `${importedSection.id}_${langCode}_all`;
      
      const userResolutionTitle = translationResolutions[conflictKeyTitle];
      const userResolutionDescription = translationResolutions[conflictKeyDescription];
      const userResolutionAll = translationResolutions[conflictKeyAll];
      
      // Déterminer la résolution effective (priorité: spécifique > général)
      const effectiveResolution = userResolutionTitle || userResolutionDescription || userResolutionAll || 'create_schema_specific';
      
      if (effectiveResolution === 'use_global') {
        // Utiliser la traduction globale - ne rien faire, ignorer l'importée
        continue;
      }
      
      if (effectiveResolution === 'overwrite_global' && existingTranslation) {
        // Écraser la traduction globale avec la valeur importée
        await translations.updateTranslation(existingTranslation.id, {
          reference_id: currentSection.id,
          language_code: langCode,
          titre: translationData.titre || '',
          description: translationData.description || ''
        });
        report.translations_updated++;
        
      } else if (effectiveResolution === 'create_schema_specific') {
        // Les traductions schéma-spécifiques sont gérées dans createSchemaInDatabase
        // Ne rien faire ici, elles seront créées plus tard
        
      } else if (!existingTranslation) {
        // Pas de traduction existante, créer une nouvelle (cas classique)
        await translations.createTranslation({
          reference_id: currentSection.id,
          language_code: langCode,
          titre: translationData.titre || '',
          description: translationData.description || ''
        });
        report.translations_created++;
      }
    }
  };

  const processConditions = async (
    importedSection: { 
      id: string; 
      conditions?: Array<{ condition_id: string; condition_value: string }> 
    }, 
    existingSectionsMap: Map<string, { id: number }>, 
    report: ImportReport
  ): Promise<void> => {
    if (!importedSection.conditions) return;
    
    const currentSection = existingSectionsMap.get(importedSection.id);
    if (!currentSection) return;
    
    const allConfigs = await fetchConfigsAdmin();
    const existingConfigs = allConfigs.filter(c => c.reference_id === currentSection.id);
    
    for (const conditionData of importedSection.conditions) {
      const conditionSection = existingSectionsMap.get(conditionData.condition_id);
      if (!conditionSection) continue;
      
      const existingConfig = existingConfigs.find(c => 
        c.condition_id === conditionSection.id && 
        c.condition_value === conditionData.condition_value
      );
      
      if (!existingConfig) {
        const { createConfig } = await import('../api/field_config');
        await createConfig({
          reference_id: currentSection.id,
          condition_id: conditionSection.id,
          condition_value: conditionData.condition_value
        });
        report.conditions_created++;
      }
    }
  };

  const ensureHierarchyExists = async (
    sectionName: string, 
    existingSectionsMap: Map<string, { id: number; name: string; ordre?: number }>
  ): Promise<void> => {
    const parentName = SectionUtils.getParentName(sectionName);
    if (parentName && !existingSectionsMap.has(parentName)) {
      await ensureHierarchyExists(parentName, existingSectionsMap);
      
      const grandParentName = SectionUtils.getParentName(parentName);
      const siblings = Array.from(existingSectionsMap.values()).filter(s => {
        const sParentName = SectionUtils.getParentName(s.name);
        return sParentName === grandParentName;
      });
      const nextOrder = siblings.length > 0 ? Math.max(...siblings.map(s => s.ordre || 0)) + 1 : 1;
      
      const newParentData = {
        name: parentName,
        type: '',
        display: true,
        ordre: nextOrder,
        importance: 0,
        mandatory_entry: false
      };
      
      const createdParents = await sections.createSections([newParentData]);
      const createdParent = createdParents[0];
      existingSectionsMap.set(parentName, createdParent);
    }
  };

  const createSchemaInDatabase = async (
    schema: SchemaJson, 
    existingSectionsMap: Map<string, { id: number }>, 
    report: ImportReport & { schema_created?: { uuid: string; appellation: string; sections_assigned: number; schema_translations_created: number } }
  ): Promise<void> => {
    const sectionIds: number[] = [];
    const processedSections = new Set<string>();
    
    const collectParentsAndSelf = (sectionName: string): void => {
      if (processedSections.has(sectionName)) return;
      
      const section = existingSectionsMap.get(sectionName);
      if (section) {
        processedSections.add(sectionName);
        sectionIds.push(section.id);
      }
      
      const parentName = SectionUtils.getParentName(sectionName);
      if (parentName) {
        collectParentsAndSelf(parentName);
      }
    };

    // Collecter les sections à inclure dans le schéma
    for (const importedSection of schema.section_list) {
      const sectionName = importedSection.id;
      
      // Vérifier si cette section est alignée avec une section existante
      const similarChoice = similarSectionChoices[sectionName];
      if (similarChoice && similarChoice.action === 'align_with_existing' && similarChoice.target_section) {
        // Utiliser la section cible pour l'alignement
        collectParentsAndSelf(similarChoice.target_section);
        console.log(`🔗 Section alignée: ${sectionName} → ${similarChoice.target_section}`);
        continue; // Passer à la section suivante
      }
      
      // Vérifier si cette section doit être ignorée (seulement si pas alignée)
      const action = newSectionActions[sectionName] || 'create';
      if (action === 'skip') {
        console.log(`⏭️ Nouvelle section ignorée par choix utilisateur: ${sectionName}`);
        continue; // Ignorer cette section
      }
      
      // Utiliser la section importée normale
      collectParentsAndSelf(sectionName);
    }

    // Ajouter les sections supprimées que l'utilisateur veut conserver
    for (const sectionId of keepRemovedSections) {
      if (!sectionIds.includes(sectionId)) {
        sectionIds.push(sectionId);
      }
    }

    let resultSchema: { uuid: string; appellation: string };

    // Vérifier si on doit mettre à jour un schéma existant ou en créer un nouveau
    if (schemaConflict.value && schemaConflict.value.action === 'update' && schemaConflict.value.existing_schema) {
      console.log(`🔄 Mise à jour du schéma existant: ${schemaConflict.value.existing_schema.appellation}`);
      
      // Mettre à jour le schéma existant
      const updateData = {
        appellation: editableSchema.appellation.trim(),
        type: editableSchema.type,
        description: editableSchema.description.trim(),
        section_list: sectionIds
      };
      
      resultSchema = await schemas.updateSchema(schemaConflict.value.existing_schema.uuid, {
        appellation: updateData.appellation,
        type: updateData.type,
        description: updateData.description,
        section_list: updateData.section_list
      });
      
    } else {
      console.log(`✨ Création d'un nouveau schéma: ${editableSchema.appellation}`);
      
      // Créer un nouveau schéma
      const schemaCreateData = {
        appellation: editableSchema.appellation.trim(),
        type: editableSchema.type,
        description: editableSchema.description.trim(),
        section_list: sectionIds
      };
      
      resultSchema = await schemas.createSchema(schemaCreateData);
    }
    
    // Créer les traductions spécifiques au schéma si nécessaire
    let schemaTranslationsCreated = 0;
    
    // Traiter les traductions des sections existantes
    for (const importedSection of schema.section_list) {
      if (!importedSection.translations) continue;
      
      const currentSection = existingSectionsMap.get(importedSection.id);
      if (!currentSection) continue;
      
      for (const [langCode, translationData] of Object.entries(importedSection.translations)) {
        const conflictKeyTitle = `${importedSection.id}_${langCode}_titre`;
        const conflictKeyDescription = `${importedSection.id}_${langCode}_description`;
        const conflictKeyAll = `${importedSection.id}_${langCode}_all`;
        
        const shouldCreateSchemaSpecific = (
          translationResolutions[conflictKeyTitle] === 'create_schema_specific' ||
          translationResolutions[conflictKeyDescription] === 'create_schema_specific' ||
          translationResolutions[conflictKeyAll] === 'create_schema_specific'
        );
        
        if (shouldCreateSchemaSpecific) {
          try {
            await schemaTranslations.createTranslation({
              schema_uuid: resultSchema.uuid,
              section_id: currentSection.id,
              language_code: langCode,
              titre: (translationData as { titre?: string; description?: string }).titre || '',
              description: (translationData as { titre?: string; description?: string }).description || ''
            });
            schemaTranslationsCreated++;
          } catch (error) {
            console.error(`❌ Erreur création traduction schéma-spécifique:`, error);
          }
        }
      }
    }

    // Traiter les traductions des nouvelles sections
    for (const importedSection of schema.section_list) {
      if (!importedSection.translations) continue;
      
      const currentSection = existingSectionsMap.get(importedSection.id);
      if (!currentSection) continue;
      
      for (const [langCode, translationData] of Object.entries(importedSection.translations)) {
        const newSectionKey = `${importedSection.id}_${langCode}_all`;
        const resolution = newSectionTranslationResolutions[newSectionKey];
        
        if (resolution === 'create_schema_specific') {
          try {
            await schemaTranslations.createTranslation({
              schema_uuid: resultSchema.uuid,
              section_id: currentSection.id,
              language_code: langCode,
              titre: (translationData as { titre?: string; description?: string }).titre || '',
              description: (translationData as { titre?: string; description?: string }).description || ''
            });
            schemaTranslationsCreated++;
            console.log(`✅ Traduction schéma-spécifique créée pour nouvelle section: ${importedSection.id} (${langCode})`);
          } catch (error) {
            console.error(`❌ Erreur création traduction nouvelle section:`, error);
          }
        } else if (resolution === 'create_global') {
          try {
            await translations.createTranslation({
              reference_id: currentSection.id,
              language_code: langCode,
              titre: (translationData as { titre?: string; description?: string }).titre || '',
              description: (translationData as { titre?: string; description?: string }).description || ''
            });
            report.translations_created++;
            console.log(`✅ Traduction globale créée pour nouvelle section: ${importedSection.id} (${langCode})`);
          } catch (error) {
            console.error(`❌ Erreur création traduction globale nouvelle section:`, error);
          }
        } else if (resolution === 'ignore') {
          console.log(`⏭️ Traduction ignorée pour nouvelle section: ${importedSection.id} (${langCode})`);
        }
      }
    }
    
    report.schema_created = {
      uuid: resultSchema.uuid,
      appellation: resultSchema.appellation,
      sections_assigned: sectionIds.length,
      schema_translations_created: schemaTranslationsCreated
    };
  };

  const validateSchemaType = (type: string | undefined): string => {
    const validTypes = ['Acquisition', 'Processing', 'Analyse', 'Autre'];
    if (!type) return '';
    
    if (validTypes.includes(type)) return type;
    
    const lowerType = type.toLowerCase();
    const match = validTypes.find(validType => 
      validType.toLowerCase().includes(lowerType) || lowerType.includes(validType.toLowerCase())
    );
    
    return match || '';
  };

  const detectExistingSchema = (
    importedSchema: { uuid?: string; appellation: string; type: string }, 
    existingSchemas: Array<{ uuid: string; appellation: string; type: string }>
  ): SchemaConflict => {
    // Vérifier correspondance UUID exacte
    if (importedSchema.uuid) {
      const uuidMatch = existingSchemas.find(s => s.uuid === importedSchema.uuid);
      if (uuidMatch) {
        return {
          type: 'uuid_match',
          existing_schema: uuidMatch,
          action: 'update'
        };
      }
    }
    
    // Vérifier correspondance nom exacte
    const exactNameMatch = existingSchemas.find(s => 
      s.appellation.toLowerCase() === importedSchema.appellation.toLowerCase() &&
      s.type === importedSchema.type
    );
    
    if (exactNameMatch) {
      return {
        type: 'name_match',
        existing_schema: exactNameMatch
      };
    }
    
    // Détecter les schémas similaires avec seuil de 70%
    const similarSchemas = detectSimilarSchemas(
      importedSchema.appellation, 
      importedSchema.type, 
      existingSchemas, 
      0.7
    );
    
    if (similarSchemas.length > 0) {
      const topSimilar = similarSchemas[0];
      return {
        type: 'similar_schema',
        existing_schema: topSimilar.schema,
        similar_schemas: similarSchemas,
        similarity_percentage: Math.round(topSimilar.similarity * 100),
        action: 'create_new' // Action par défaut, l'utilisateur peut changer
      };
    }
    
    return { type: 'none' };
  };

  const detectRemovedSections = async (
    existingSchemaUuid: string,
    newSectionList: Array<{ id: string }>,
    existingTranslations: unknown[]
  ): Promise<Array<{
    id: number;
    name: string;
    type?: string;
    has_translations: boolean;
    usage_count?: number;
  }>> => {
    try {
      // Récupérer les sections du schéma existant
      const existingSchemaSections = await schemas.fetchSectionsBySchema(existingSchemaUuid);
      
      // Créer un Set des nouveaux noms de sections pour recherche rapide
      const newSectionNames = new Set(newSectionList.map(section => section.id));
      
      // Trouver les sections qui étaient dans l'ancien schéma mais pas dans le nouveau
      const removedSections: Array<{
        id: number;
        name: string;
        type?: string;
        has_translations: boolean;
        usage_count?: number;
      }> = [];
      
      for (const section of existingSchemaSections) {
        if (!newSectionNames.has(section.name)) {
          const hasTranslations = (existingTranslations as Array<{ reference_id: number }>)
            .some(translation => translation.reference_id === section.id);
          
          removedSections.push({
            id: section.id,
            name: section.name,
            type: section.type,
            has_translations: hasTranslations,
            usage_count: 0
          });
        }
      }
      
      return removedSections;
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du schéma existant:', error);
      return [];
    }
  };

  // Réinitialisation
  const reset = (): void => {
    step.value = 1;
    selectedFile.value = null;
    parsedSchema.value = null;
    importReport.value = null;
    errorMessage.value = '';
    parsing.value = false;
    importing.value = false;
    schemaConflict.value = null;
    schemaChanges.value = null;
    
    editableSchema.appellation = '';
    editableSchema.type = '';
    editableSchema.description = '';
    
    Object.keys(resolutions).forEach(key => delete resolutions[key]);
    Object.keys(translationResolutions).forEach(key => delete translationResolutions[key]);
    Object.keys(newSectionTranslationResolutions).forEach(key => delete newSectionTranslationResolutions[key]);
    Object.keys(similarSectionChoices).forEach(key => delete similarSectionChoices[key]);
    Object.keys(newSectionActions).forEach(key => delete newSectionActions[key]);
    excludedSections.clear();
    keepRemovedSections.clear();
    keepRemovedTranslations.clear();
    Object.keys(updateTranslationResolutions).forEach(key => delete updateTranslationResolutions[key]);
  };

  // Utilitaires pour Step3
  const updateResolution = (conflictId: string, resolution: string): void => {
    resolutions[conflictId] = resolution;
  };

  const updateTranslationResolution = (sectionName: string, languageCode: string, fieldType: string, resolution: string): void => {
    const key = `${sectionName}_${languageCode}_${fieldType}`;
    translationResolutions[key] = resolution;
  };

  const updateNewSectionTranslationResolution = (sectionName: string, languageCode: string, resolution: string): void => {
    const key = `${sectionName}_${languageCode}_all`;
    newSectionTranslationResolutions[key] = resolution;
  };

  const updateSimilarSectionChoice = (sectionId: string, action: 'create_new' | 'align_with_existing', targetSection?: string): void => {
    similarSectionChoices[sectionId] = { action, target_section: targetSection };
  };

  const updateNewSectionAction = (sectionId: string, action: 'create' | 'skip'): void => {
    newSectionActions[sectionId] = action;
  };

  const updateRemovedSectionAction = (sectionId: number, action: 'keep' | 'remove'): void => {
    if (action === 'keep') {
      keepRemovedSections.add(sectionId);
    } else {
      keepRemovedSections.delete(sectionId);
    }
  };

  const getNewSectionTranslationResolution = (conflict: { section_name: string; language_code: string }): string => {
    const key = `${conflict.section_name}_${conflict.language_code}_all`;
    return newSectionTranslationResolutions[key] || 'create_schema_specific';
  };

  return {
    // State
    step,
    selectedFile,
    parsedSchema,
    importReport,
    errorMessage,
    parsing,
    importing,
    schemaConflict,
    schemaChanges,
    editableSchema,
    resolutions,
    translationResolutions,
    newSectionTranslationResolutions,
    similarSectionChoices,
    newSectionActions,
    excludedSections,
    keepRemovedSections,
    keepRemovedTranslations,
    updateTranslationResolutions,
    
    // Methods
    parseFile,
    executeImport,
    reset,
    
    // Step3 utilities
    updateResolution,
    updateTranslationResolution,
    updateNewSectionTranslationResolution,
    getNewSectionTranslationResolution,
    updateSimilarSectionChoice,
    updateNewSectionAction,
    updateRemovedSectionAction
  };
} 